import { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import { DatenProvider } from './daten/kontext';
import type { Datenquelle } from './daten/quelle';
import type { Stand } from './daten/typen';
import { demoQuelle } from './daten/demoQuelle';
import { konfig } from './konfig';
import { Anmeldung } from './ui/Anmeldung';
import { DozentDemo } from './dozent/DozentDemo';
import './stil.css';
import './dozent/dozent.css';

function Start() {
  const [ansicht, setAnsicht] = useState(() => (location.hash === '#dozent' ? 'dozent' : 'verwaltung'));
  useEffect(() => {
    const h = () => setAnsicht(location.hash === '#dozent' ? 'dozent' : 'verwaltung');
    window.addEventListener('hashchange', h); return () => window.removeEventListener('hashchange', h);
  }, []);
  if (!konfig.supabaseUrl && ansicht === 'dozent') return <DozentDemo zurVerwaltung={() => { location.hash = ''; }} />;
  return <Verwaltung />;
}

function Verwaltung() {
  const [bereit, setBereit] = useState<{ quelle: Datenquelle; stand: Stand; abmelden?: () => void } | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [anmelden, setAnmelden] = useState<null | ((email: string) => Promise<void>)>(null);
  const [gesendet, setGesendet] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        if (!konfig.supabaseUrl) {
          const q = demoQuelle('admin');
          setBereit({ quelle: q, stand: await q.laden() });
          return;
        }
        const { supabaseClient, supabaseQuelle, adminMandanten } = await import('./daten/supabaseQuelle');
        const sb = supabaseClient(konfig.supabaseUrl, konfig.supabaseAnonKey);
        const starten = async () => {
          const m = await adminMandanten(sb);
          if (!m.length) { setFehler('Ihr Konto hat keine Admin-Rechte für die Planung. Bitte wenden Sie sich an die Verwaltung.'); return; }
          const q = supabaseQuelle(sb, m[0].id);
          setBereit({ quelle: q, stand: await q.laden(), abmelden: async () => { await sb.auth.signOut(); location.reload(); } });
        };
        const { data } = await sb.auth.getSession();
        if (data.session) await starten();
        else {
          setAnmelden(() => async (email: string) => {
            const { error } = await sb.auth.signInWithOtp({ email, options: { emailRedirectTo: location.href, shouldCreateUser: false } });
            if (error) throw error;
            setGesendet(true);
          });
          sb.auth.onAuthStateChange((ereignis) => { if (ereignis === 'SIGNED_IN') void starten(); });
        }
      } catch (e) { setFehler(String((e as Error).message ?? e)); }
    })();
  }, []);

  if (fehler) return <div className="startbild"><h1>MeisterMind Planung</h1><p className="hinweisbox fehler">{fehler}</p></div>;
  if (bereit) return <DatenProvider quelle={bereit.quelle} start={bereit.stand}><App abmelden={bereit.abmelden} /></DatenProvider>;
  if (anmelden) return <Anmeldung titel="MeisterMind Planung" anmelden={anmelden} gesendet={gesendet} />;
  return <div className="startbild"><div className="lader" aria-label="Wird geladen" /></div>;
}

// Service Worker (Push-Nachrichten bei Absagen) nur im Betrieb über https
if ('serviceWorker' in navigator && location.protocol === 'https:' && konfig.supabaseUrl) {
  window.addEventListener('load', () => { navigator.serviceWorker.register('/sw.js').catch(() => { /* ohne Push weiter */ }); });
}

createRoot(document.getElementById('root')!).render(<StrictMode><Start /></StrictMode>);
